import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    OnDestroy,
    ViewChild,
    ViewEncapsulation,
    inject, OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { TranslocoModule } from '@ngneat/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { ReportService } from '../../reports.service';
import { SendReportComponent } from '../send-report/send-report.component';
import { ReportDataTableListComponent } from 'app/ui/report-data-table-list/report-data-table-list.component';
import { MessageChartComponent } from 'app/company/ai-chat/agent-chat/charts/message-chart/message-chart.component';
import { ChatService } from 'app/company/ai-chat/agent-chat/chat.service';
import { Report } from '../report';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { injectResponsiveNet } from 'app/modules/shared/utils/responsive-document-net.util';
import {
    PRIMARY_REPORT_LANGUAGE_CODES,
    REPORT_LANGUAGE_BY_CODE,
    REPORT_LANGUAGES,
    ReportLanguageOption,
} from './report-languages';
import {
    ReportSlide,
    discoverSlides,
    enterPresentation,
    exitPresentation,
    setActiveSlide,
} from './report-presentation.util';

type HtmlLoadResult =
    | { kind: 'html'; html: string }
    | { kind: 'legacy' }
    | { kind: 'empty' };

/** Any language code the picker offers (English is the untranslated original). */
type ReportLang = string;

const REPORT_NAV_LABELS: Record<string, string> = {
    'scope': 'scope',
    'access': 'access',
    'examples': 'examples',
    'overview': 'overview',
    'read the overview': 'overview',
    'jump to content scope': 'scope',
};

@Component({
    selector: 'app-view-new-report',
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatMenuModule,
    ],
    templateUrl: './view-new-report.component.html',
    styleUrls: ['./view-new-report.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewNewReportComponent implements OnDestroy, OnInit {
    @ViewChild('previewFrame') previewFrame?: ElementRef<HTMLIFrameElement>;

    private route = inject(ActivatedRoute);
    private reportService = inject(ReportService);
    private chatService = inject(ChatService);
    private dialog = inject(MatDialog);
    private alert = inject(CustomAlertService);
    private cdr = inject(ChangeDetectorRef);
    private destroyRef = inject(DestroyRef);

    reportId = '';
    loading = true;
    loadError = '';
    showPreview = false;
    hasData = false;
    /** Gate for the View Chart action (meta `hasChart` / session+message ids). */
    hasChart = false;
    reportType: string | undefined;
    reportName = '';
    /** Chat session + linked message ids for `ai-chat/message-data/:sessionId/:messageId`. */
    private sessionId: string | null = null;
    private messageIds: Array<string | number> = [];

    currentLang: ReportLang = 'en';
    translating = false;

    /* ------------------------------- Presentation mode ------------------------------- */

    /** True while the report is being presented full screen. */
    presenting = false;
    /** Sections discovered in the report document; empty means the mode is unavailable. */
    slides: ReportSlide[] = [];
    slideIndex = 0;
    /** True while the browser is actually in fullscreen, which it may refuse to enter. */
    isFullscreen = false;
    /** Element focused before entering, restored on exit so the keyboard user is where they were. */
    private returnFocusTo: HTMLElement | null = null;
    /**
     * Keep the fullscreen flag in step with the browser, and ONLY that.
     *
     * Leaving fullscreen deliberately does not leave the presentation: the overlay
     * is a fixed, self-sufficient layer and reads perfectly well in a window, so a
     * presenter who hits Escape or F11 to get their tab bar back should not be
     * dropped to slide one. Escape ends the presentation through the key handler,
     * which is where that decision belongs; in fullscreen the browser eats the
     * first Escape for itself, so it takes two — exactly what every other
     * fullscreen application does.
     */
    private readonly onFullscreenChange = (): void => {
        this.isFullscreen = !!document.fullscreenElement;
        this.cdr.markForCheck();
    };

    /** English and Arabic, pinned to the top of the picker. */
    readonly primaryLanguages: readonly ReportLanguageOption[] = REPORT_LANGUAGES.filter(lang =>
        PRIMARY_REPORT_LANGUAGE_CODES.includes(lang.code)
    );
    /** All remaining offered languages, shown below the primary ones. */
    readonly otherLanguages: readonly ReportLanguageOption[] = REPORT_LANGUAGES.filter(
        lang => !PRIMARY_REPORT_LANGUAGE_CODES.includes(lang.code)
    );

    private pendingHtml: string | null = null;
    /**
     * Remount budget for the blob-URL load path. `assignIframeUrl` asks for a
     * remount whenever it cannot reach the frame same-origin, and a remount that
     * keeps failing for a structural reason (an over-tight `sandbox`, a future
     * browser policy on blob: navigation) would otherwise retry forever behind a
     * blank page - which is exactly how this feature went dark in production.
     * After the budget is spent the component renders via `srcdoc` instead, so a
     * report always reaches the screen even if section navigation degrades.
     */
    private iframeRemountAttempts = 0;
    private static readonly MAX_IFRAME_REMOUNTS = 2;
    private reportHtmlContent: string | null = null;
    private iframeBlobUrl: string | null = null;
    private htmlByLang: Record<string, string> = {};
    /**
     * Pre-generated translation HTML URLs keyed by language code (e.g. `ar`, `fr`), sourced
     * from the report meta `translation` map and the list-page hand-off. Fetched through
     * `ReportService.getReportHtmlByUrl()` (authenticated `HttpClient`) rather than assigned
     * straight to the iframe, so `Authorization` reaches the `report/get-file` request.
     */
    private translationUrls: Record<string, string> = {};

    /** English display name of the currently rendered language. */
    get currentLanguageName(): string {
        return REPORT_LANGUAGE_BY_CODE.get(this.currentLang)?.name ?? this.currentLang.toUpperCase();
    }

    ngOnDestroy(): void {
        this.revokeIframeBlob();
        // Presentation mode holds a document listener and may have put the browser
        // into fullscreen; leaving either behind survives the component.
        this.stopPresenting();
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    }

    ngOnInit(): void {
        this.route.paramMap
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((params) => {
                    this.reportId = params.get('reportId') ?? '';
                    this.loading = true;
                    this.loadError = '';
                    this.showPreview = false;
                    this.hasData = false;
                    this.hasChart = false;
                    this.sessionId = null;
                    this.messageIds = [];
                    this.reportType = undefined;
                    this.reportName = '';
                    this.currentLang = 'en';
                    this.translating = false;
                    this.htmlByLang = {};
                    this.translationUrls = {};
                    const arUrl = this.consumeStashedTranslationUrl(this.reportId);
                    if (arUrl) {
                        this.translationUrls['ar'] = arUrl;
                    }
                    this.pendingHtml = null;
                    this.reportHtmlContent = null;
                    this.cdr.markForCheck();

                    if (!this.reportId) {
                        return of({ html: '', meta: null as Report | null });
                    }

                    return forkJoin({
                        html: this.reportService.getReportHtml(this.reportId),
                        meta: this.reportService
                            .getOneReport(this.reportId)
                            .pipe(catchError(() => of(null))),
                    });
                })
            )
            .subscribe({
                next: ({ html, meta }) => {
                    this.applyReportMeta(meta);
                    this.handleResponse(html ?? '');
                },
                error: () => {
                    this.loading = false;
                    this.loadError = 'Failed to load report';
                    this.cdr.markForCheck();
                },
            });
    }

    private handleResponse(body: string): void {
        const result = this.parseResponseBody(body);

        if (result.kind === 'legacy') {
            this.loading = false;
            this.loadError = 'Unable to display this report';
            this.cdr.markForCheck();
            return;
        }

        if (result.kind === 'empty') {
            this.loading = false;
            this.loadError = 'Report content is empty';
            this.cdr.markForCheck();
            return;
        }

        this.htmlByLang[this.currentLang] = result.html;
        this.loading = false;
        this.renderReportHtml(result.html);
    }

    /** User-facing status while the report is being translated or loaded in another language. */
    getTranslatingMessage(): string {
        return `Translating into ${this.currentLanguageName}`;
    }

    /** Render the report in the given language (`en` = original), translating on demand. */
    setLanguage(lang: ReportLang): void {
        if (lang === this.currentLang || this.translating || this.loading) {
            return;
        }

        const previousLang = this.currentLang;
        this.currentLang = lang;

        const cached = this.htmlByLang[lang];
        if (cached) {
            this.renderReportHtml(cached);
            return;
        }

        this.translating = true;
        this.cdr.markForCheck();

        // Report already has a pre-generated translation: fetch its HTML through the
        // authenticated HttpClient (never assign it straight to the iframe — a raw
        // cross-origin navigation cannot carry the Authorization header).
        const translationUrl = this.translationUrls[lang];
        const html$ = translationUrl
            ? this.reportService.getReportHtmlByUrl(translationUrl)
            : this.reportService.getReportHtmlForTranslation(this.reportId, lang, false);

        html$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (body: string) => {
                    const result = this.parseResponseBody(body ?? '');
                    if (result.kind !== 'html') {
                        this.translating = false;
                        this.currentLang = previousLang;
                        this.alert.failedMessage(
                            'Failed to load the translated report.'
                        );
                        this.cdr.markForCheck();
                        return;
                    }

                    this.htmlByLang[lang] = result.html;
                    this.translating = false;
                    this.renderReportHtml(result.html);
                },
                error: () => {
                    this.translating = false;
                    this.currentLang = previousLang;
                    this.alert.failedMessage(
                        'Failed to load the translated report.'
                    );
                    this.cdr.markForCheck();
                },
            });
    }

    private renderReportHtml(html: string): void {
        this.pendingHtml = html;
        this.showPreview = true;
        this.iframeRemountAttempts = 0;
        this.cdr.markForCheck();
        this.scheduleIframeRender();
    }

    /** Assign the iframe source (inline HTML blob). */
    private scheduleIframeRender(): void {
        setTimeout(() => this.writeToIframe(), 0);
        setTimeout(() => this.writeToIframe(), 100);
    }

    private writeToIframe(): void {
        const iframe = this.previewFrame?.nativeElement;
        if (!iframe) {
            return;
        }

        this.writeHtmlToIframe(iframe);
    }

    private writeHtmlToIframe(iframe: HTMLIFrameElement): void {
        const html = this.pendingHtml;
        if (!html?.trim()) {
            return;
        }

        const prepared = this.prepareHtmlForInDocumentNav(html);
        this.reportHtmlContent = prepared;
        this.pendingHtml = null;

        // New document instance on each render; allow navigation to be re-wired.
        delete iframe.dataset['reportNavWired'];

        const onLoad = (): void => {
            this.wireIframeNavigation(iframe);
            // A fresh document means the old slide marks are gone with it, so the
            // list is rebuilt rather than reused. Translating a report re-renders
            // it, and the translated headings are the ones the chrome must show.
            this.refreshSlides(iframe);
            iframe.removeEventListener('load', onLoad);
        };

        this.revokeIframeBlob();
        const blob = new Blob([prepared], { type: 'text/html;charset=utf-8' });
        this.iframeBlobUrl = URL.createObjectURL(blob);
        iframe.removeAttribute('srcdoc');

        if (!this.assignIframeUrl(iframe, this.iframeBlobUrl, onLoad)) {
            if (
                this.iframeRemountAttempts <
                ViewNewReportComponent.MAX_IFRAME_REMOUNTS
            ) {
                this.iframeRemountAttempts++;
                // Re-queue HTML so the remounted iframe can pick it up.
                this.pendingHtml = prepared;
                this.remountPreviewFrame();
                return;
            }

            // The frame is unreachable same-origin and remounting has not helped,
            // so the blob URL will never load. `srcdoc` still paints the document
            // (it needs no origin access), which keeps the report readable instead
            // of leaving the user on a blank page.
            this.revokeIframeBlob();
            iframe.addEventListener('load', onLoad);
            iframe.srcdoc = prepared;
        }
    }

    /**
     * Load a (same-origin blob) URL into the preview iframe without pushing joint session
     * history. Assigning `iframe.src` on each language switch enables the browser Back button
     * and can land on a revoked blob URL ("resource not found"). Prefer `location.replace`;
     * remount the iframe when the probe below fails.
     *
     * @returns false when the caller must remount and retry the load
     */
    private assignIframeUrl(
        iframe: HTMLIFrameElement,
        url: string,
        onLoad: () => void,
        onError?: () => void
    ): boolean {
        try {
            // Probe same-origin access before attaching listeners.
            void iframe.contentWindow?.location?.href;
        } catch {
            return false;
        }

        iframe.addEventListener('load', onLoad);
        if (onError) {
            iframe.addEventListener('error', onError);
        }

        try {
            const win = iframe.contentWindow;
            const href = win?.location?.href ?? '';
            if (win && href && href !== 'about:blank') {
                win.location.replace(url);
                return true;
            }
        } catch {
            iframe.removeEventListener('load', onLoad);
            if (onError) {
                iframe.removeEventListener('error', onError);
            }
            return false;
        }

        iframe.src = url;
        return true;
    }

    /** Destroy and recreate the preview iframe so nested browsing-context history is dropped. */
    private remountPreviewFrame(): void {
        this.showPreview = false;
        this.cdr.detectChanges();
        this.showPreview = true;
        this.cdr.detectChanges();
        this.scheduleIframeRender();
    }

    private revokeIframeBlob(): void {
        if (this.iframeBlobUrl) {
            URL.revokeObjectURL(this.iframeBlobUrl);
            this.iframeBlobUrl = null;
        }
    }

    showDatatable(): void {
        if (!this.reportId) {
            return;
        }

        this.reportService
            .getReportsData(this.reportId, this.resolvedReportType())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res: { data?: { data_table?: unknown } }) => {
                const dataTable = res?.data?.data_table;
                if (!dataTable) {
                    this.alert.failedMessage(
                        'No table data is available for this report.'
                    );
                    return;
                }

                this.dialog.open(ReportDataTableListComponent, {
                    width: '70%',
                    maxHeight: '80dvh',
                    panelClass: 'biggDialog',
                    data: {
                        dataTable,
                        from: this.isArrayOfArrays(dataTable as unknown[])
                            ? 'new-report'
                            : 'newest-report',
                        name: this.reportName,
                    },
                });
                },
                error: () => {
                    this.alert.failedMessage(
                        'Failed to load report data. Please try again.'
                    );
                },
            });
    }

    /**
     * View Chart click: ONLY `GET ai-chat/message-data/:sessionId/:messageId`.
     * IDs come from the page-load `get-one-report` meta — never re-fetch report APIs here.
     */
    showCharts(): void {
        if (!this.sessionId || !this.messageIds.length) {
            this.alert.failedMessage('No chart data available');
            return;
        }
        this.loadChartsFromMessageData(this.sessionId, this.messageIds);
    }

    /** Agent-chat shape: GET ai-chat/message-data/:sessionId/:messageId */
    private loadChartsFromMessageData(
        sessionId: string,
        messageIds: Array<string | number>
    ): void {
        if (messageIds.length === 1) {
            const messageId = messageIds[0];
            this.chatService
                .getMessageData(sessionId, messageId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (res: unknown) => {
                        if (!res) {
                            this.alert.failedMessage('No chart data available');
                            return;
                        }
                        this.openMessageChartDialog(res, sessionId, messageId);
                    },
                    error: (err: { error?: { message?: string } }) => {
                        this.alert.failedMessage(
                            err?.error?.message ??
                                'Failed to load chart data. Please try again.'
                        );
                    },
                });
            return;
        }

        forkJoin(
            messageIds.map(messageId =>
                this.chatService
                    .getMessageData(sessionId, messageId)
                    .pipe(catchError(() => of(null)))
            )
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (results: unknown[]) => {
                    const queries = results
                        .filter(
                            (item): item is Record<string, unknown> =>
                                !!item && typeof item === 'object'
                        )
                        .flatMap((message) => {
                            const q = (message as { queries?: unknown }).queries;
                            return Array.isArray(q) ? q : [];
                        });
                    if (!queries.length) {
                        this.alert.failedMessage('No chart data available');
                        return;
                    }
                    this.openMessageChartDialog(
                        { queries },
                        sessionId,
                        messageIds[0]
                    );
                },
                error: () => {
                    this.alert.failedMessage(
                        'Failed to load chart data. Please try again.'
                    );
                },
            });
    }

    private openMessageChartDialog(
        chartData: unknown,
        sessionId: string,
        messageId: string | number
    ): void {
        this.dialog.open(MessageChartComponent, {
            width: '70%',
            maxHeight: '90dvh',
            panelClass: 'biggDialog',
            data: {
                chartData,
                sessionId,
                messageId,
                from: 'chat',
            },
            autoFocus: false,
        });
    }

    /** Read (and clear) the Arabic translation URL handed over from the reports list. */
    private consumeStashedTranslationUrl(reportId: string): string | null {
        if (!reportId) {
            return null;
        }
        const key = `report_ar_translation:${reportId}`;
        try {
            const url = localStorage.getItem(key);
            localStorage.removeItem(key);
            return url;
        } catch {
            return null;
        }
    }

    private applyReportMeta(meta: Report | null): void {
        this.reportType = this.resolveReportType(meta);
        this.reportName = meta?.name ?? '';
        this.hasData = meta?.hasData === true;
        const link = this.resolveChartLink(meta);
        this.sessionId = link.sessionId;
        this.messageIds = link.messageIds;
        // View Chart needs sessionId + message ids from this meta. Do not mirror hasData —
        // that made the button appear and then hit the wrong API on click.
        this.hasChart =
            meta?.hasChart === true ||
            (!!this.sessionId && this.messageIds.length > 0);
        // Any pre-generated translation (any language) can be loaded straight from its S3 URL
        // instead of re-running the translation endpoint.
        for (const [code, url] of Object.entries(meta?.translation ?? {})) {
            if (url && !this.translationUrls[code]) {
                this.translationUrls[code] = url;
            }
        }
        this.cdr.markForCheck();
    }

    private resolveChartLink(meta: Report | null): {
        sessionId: string | null;
        messageIds: Array<string | number>;
    } {
        if (!meta) {
            return { sessionId: null, messageIds: [] };
        }
        const raw = meta as Report & Record<string, unknown>;
        let sessionId = this.coerceSessionId(
            raw.sessionId ??
                raw.session_id ??
                raw.chatSessionId ??
                raw.chat_session_id
        );
        if (!sessionId && Array.isArray(raw.sessionIds) && raw.sessionIds.length) {
            sessionId = this.coerceSessionId(raw.sessionIds[0]);
        }

        const linkSources = [
            raw.messageId,
            raw.message_id,
            raw.linked_id,
            raw.linkedId,
            raw.chatIds,
            raw.chat_ids,
            raw.messageIds,
            raw.questionnaireIds,
            raw.questionIds,
            raw.question_ids,
        ];
        const fromObjects = this.extractIdsFromLinkObjects(linkSources);
        if (!sessionId && fromObjects.sessionId) {
            sessionId = fromObjects.sessionId;
        }
        const messageIds = this.coerceMessageIds([
            ...linkSources,
            ...fromObjects.messageIds,
        ]);

        return { sessionId, messageIds };
    }

    /**
     * get-one-report may return linked rows as `{ id, sessionId }` objects
     * (or a JSON string of that shape) instead of bare ids.
     */
    private extractIdsFromLinkObjects(sources: unknown[]): {
        sessionId: string | null;
        messageIds: Array<string | number>;
    } {
        let sessionId: string | null = null;
        const messageIds: Array<string | number> = [];

        const visit = (value: unknown): void => {
            if (value == null) {
                return;
            }
            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (
                    (trimmed.startsWith('[') || trimmed.startsWith('{')) &&
                    (trimmed.includes('sessionId') || trimmed.includes('id'))
                ) {
                    try {
                        visit(JSON.parse(trimmed) as unknown);
                    } catch {
                        /* not JSON — ignore */
                    }
                }
                return;
            }
            if (Array.isArray(value)) {
                value.forEach(visit);
                return;
            }
            if (typeof value !== 'object') {
                return;
            }
            const obj = value as Record<string, unknown>;
            const sid = this.coerceSessionId(
                obj['sessionId'] ?? obj['session_id'] ?? obj['chatSessionId']
            );
            if (sid && !sessionId) {
                sessionId = sid;
            }
            const id =
                obj['id'] ??
                obj['messageId'] ??
                obj['message_id'] ??
                obj['chatId'] ??
                obj['chat_id'] ??
                obj['questionId'] ??
                obj['question_id'];
            if (id != null && typeof id !== 'object') {
                messageIds.push(id as string | number);
            }
        };

        sources.forEach(visit);
        return { sessionId, messageIds };
    }

    private coerceSessionId(raw: unknown): string | null {
        if (raw == null) {
            return null;
        }
        const value = String(raw).trim();
        return value || null;
    }

    private coerceMessageIds(sources: unknown[]): Array<string | number> {
        const collected: Array<string | number> = [];
        const pushId = (value: unknown): void => {
            if (value == null || value === '') {
                return;
            }
            if (typeof value === 'object') {
                return;
            }
            if (typeof value === 'number' && Number.isFinite(value)) {
                collected.push(value);
                return;
            }
            const asString = String(value).trim();
            if (!asString) {
                return;
            }
            if (asString.startsWith('[')) {
                try {
                    const parsed: unknown = JSON.parse(asString);
                    if (Array.isArray(parsed)) {
                        parsed.forEach(pushId);
                        return;
                    }
                } catch {
                    /* fall through */
                }
            }
            const asNumber = Number(asString);
            collected.push(
                Number.isFinite(asNumber) && asString === String(asNumber)
                    ? asNumber
                    : asString
            );
        };

        for (const source of sources) {
            if (Array.isArray(source)) {
                source.forEach(pushId);
            } else {
                pushId(source);
            }
        }

        const seen = new Set<string>();
        return collected.filter((id) => {
            const key = String(id);
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }

    private resolveReportType(meta: Report | null): string | undefined {
        const fromMeta = meta?.ReportType ?? meta?.reportType;
        if (fromMeta) {
            return fromMeta;
        }
        const fromQuery = this.route.snapshot.queryParamMap.get('reportType');
        return fromQuery?.trim() || undefined;
    }

    private resolvedReportType(): string | undefined {
        return this.reportType ?? this.route.snapshot.queryParamMap.get('reportType') ?? undefined;
    }

    private isArrayOfArrays(arr: unknown[]): boolean {
        return (
            arr.length > 0 &&
            arr.every(item => Array.isArray(item) || item === null)
        );
    }

    sendEmail(): void {
        if (!this.reportId) {
            return;
        }
        this.dialog.open(SendReportComponent, {
            width: '60%',
            maxHeight: '60dvh',
            panelClass: 'bigDialog',
            data: { id: this.reportId },
        });
    }

    downloadHtmlReport(): void {
        const html = this.reportHtmlContent;
        if (!html?.trim()) {
            return;
        }

        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.buildDownloadFileName();
        link.click();
        URL.revokeObjectURL(url);
    }

    private buildDownloadFileName(): string {
        let title = 'report';
        try {
            const doc = new DOMParser().parseFromString(
                this.reportHtmlContent ?? '',
                'text/html'
            );
            const parsed = doc.querySelector('title')?.textContent?.trim();
            if (parsed) {
                title = parsed
                    .replace(/[<>:"/\\|?*]+/g, '')
                    .replace(/\s+/g, '-')
                    .slice(0, 80);
            }
        } catch {
            /* use default */
        }
        return `${title}-${this.reportId || 'export'}.html`;
    }

    /**
     * Backend HTML nav often uses paths like /scope or /home. In srcdoc iframes those
     * navigate the parent Angular app. Rewrite to #anchors when possible.
     */
    private prepareHtmlForInDocumentNav(html: string): string {
        try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            doc.querySelectorAll('base').forEach(node => node.remove());

            doc.querySelectorAll('a[href]').forEach((anchor) => {
                anchor.setAttribute('target', '_self');
                anchor.removeAttribute('rel');

                const raw = anchor.getAttribute('href')?.trim() ?? '';
                if (!raw) {
                    return;
                }
                if (/^(https?:|mailto:|tel:|javascript:)/i.test(raw)) {
                    return;
                }

                const hashIndex = raw.indexOf('#');
                const hashPart =
                    hashIndex >= 0 ? raw.slice(hashIndex + 1).split(/[?#]/)[0] : '';
                const pathPart = raw
                    .replace(/^[#/]+/, '')
                    .split(/[?#]/)[0]
                    .replace(/^\//, '');
                const segment = (hashPart || pathPart).trim();
                const lower = segment.toLowerCase();

                if (!segment || lower === 'home' || lower === 'index') {
                    anchor.setAttribute('href', '#');
                    return;
                }

                anchor.setAttribute('href', `#${segment}`);
                anchor.setAttribute('data-report-nav', segment);
            });

            injectResponsiveNet(doc);

            return doc.documentElement.outerHTML;
        } catch {
            return html;
        }
    }

    /* ============================ Presentation mode ============================
     *
     * A report is an HTML document in an iframe; presenting it means stepping
     * through its sections one at a time, full screen. All of the DOM work lives in
     * `report-presentation.util.ts` (pure, and tested against a real document); what
     * is here is the state machine, the keyboard, and the fullscreen handshake.
     * ========================================================================= */

    /** The document being presented, or null when the frame is not ready. */
    private get reportDoc(): Document | null {
        try {
            return this.previewFrame?.nativeElement?.contentDocument ?? null;
        } catch {
            // A cross-origin frame throws here. It cannot happen for the blob: URL
            // this component creates, but a thrown getter would take the whole view
            // down, and "presentation is unavailable" is the correct degradation.
            return null;
        }
    }

    /**
     * Re-read the report's sections.
     *
     * Called on every document load. A report with no discoverable structure yields
     * an empty list, which hides the button entirely — offering a presentation that
     * opens on a single unsplittable page would be worse than not offering one.
     */
    private refreshSlides(iframe: HTMLIFrameElement): void {
        const doc = iframe.contentDocument;
        this.slides = doc ? discoverSlides(doc) : [];
        this.slideIndex = 0;
        this.cdr.markForCheck();
    }

    /** Whether the Present action should be offered at all. */
    get canPresent(): boolean {
        return this.slides.length > 1;
    }

    get currentSlideTitle(): string {
        return this.slides[this.slideIndex]?.title ?? '';
    }

    /** Progress as a percentage, for the chrome's bar. */
    get slideProgress(): number {
        if (this.slides.length < 2) return 100;
        return Math.round(((this.slideIndex + 1) / this.slides.length) * 100);
    }

    async startPresenting(): Promise<void> {
        const doc = this.reportDoc;
        // Re-entry would add a second pair of key listeners, and every step would
        // then advance twice. Cheap to guard, invisible to debug.
        if (!doc || !this.canPresent || this.presenting) return;

        this.returnFocusTo = document.activeElement as HTMLElement | null;
        this.presenting = true;
        this.slideIndex = 0;
        enterPresentation(doc);
        setActiveSlide(doc, 0);

        // Keys are listened for on BOTH documents. Focus sits in whichever the user
        // last clicked, and a presentation where the arrow keys work only until you
        // click the slide is a presentation that does not work.
        doc.addEventListener('keydown', this.onPresentationKey, true);
        document.addEventListener('keydown', this.onPresentationKey, true);

        await this.requestFullscreen();
        this.cdr.markForCheck();
    }

    /**
     * Ask for real fullscreen, and carry on without it if refused.
     *
     * The request can be denied (an iframe without `allowfullscreen`, a browser
     * policy, a user gesture the browser did not credit). The overlay is a fixed,
     * top-layer element in its own right, so presentation is fully usable either
     * way — fullscreen only removes the browser chrome around it.
     */
    private async requestFullscreen(): Promise<void> {
        const host = this.previewFrame?.nativeElement?.closest(
            '.view-new-report-page'
        ) as HTMLElement | null;
        if (!host?.requestFullscreen) return;
        document.addEventListener('fullscreenchange', this.onFullscreenChange);
        try {
            await host.requestFullscreen();
            this.isFullscreen = true;
        } catch {
            this.isFullscreen = false;
        }
    }

    /**
     * Toggle the browser's fullscreen, WITHOUT leaving the presentation.
     *
     * The two are deliberately separate controls. Presentation mode is a way of
     * reading the report and works fully in a window; fullscreen only removes the
     * browser's own chrome from around it. Collapsing them would mean a presenter
     * who wanted their tab bar back had to restart the presentation from slide one.
     */
    toggleFullscreen(): void {
        if (this.isFullscreen) {
            void document.exitFullscreen?.().catch(() => undefined);
            return;
        }
        void this.requestFullscreen().then(() => this.cdr.markForCheck());
    }

    stopPresenting(): void {
        if (!this.presenting) return;
        this.presenting = false;

        const doc = this.reportDoc;
        if (doc) {
            exitPresentation(doc);
            doc.removeEventListener('keydown', this.onPresentationKey, true);
        }
        document.removeEventListener('keydown', this.onPresentationKey, true);

        if (document.fullscreenElement) {
            void document.exitFullscreen?.().catch(() => undefined);
        }
        this.returnFocusTo?.focus?.();
        this.returnFocusTo = null;
        this.cdr.markForCheck();
    }

    goToSlide(index: number): void {
        const doc = this.reportDoc;
        if (!doc || !this.slides.length) return;
        const next = Math.max(0, Math.min(this.slides.length - 1, index));
        if (next === this.slideIndex && doc.querySelector('.sai-slide-active')) return;
        this.slideIndex = next;
        setActiveSlide(doc, next);
        this.cdr.markForCheck();
    }

    nextSlide(): void {
        this.goToSlide(this.slideIndex + 1);
    }

    previousSlide(): void {
        this.goToSlide(this.slideIndex - 1);
    }

    /**
     * Presentation keyboard.
     *
     * An arrow bound to a DIRECTION rather than to next/previous: in an RTL report
     * the left arrow advances, because that is the way the content runs and the way
     * every reader of that language expects a deck to move. `PageDown`/`PageUp` are
     * bound too, because that is what a physical presenter remote sends.
     */
    private readonly onPresentationKey = (event: KeyboardEvent): void => {
        if (!this.presenting) return;

        const rtl = this.reportDoc?.documentElement?.dir === 'rtl';
        const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
        const backward = rtl ? 'ArrowRight' : 'ArrowLeft';

        let handled = true;
        switch (event.key) {
            case forward:
            case 'PageDown':
            case ' ':
            case 'Enter':
                this.nextSlide();
                break;
            case backward:
            case 'PageUp':
            case 'Backspace':
                this.previousSlide();
                break;
            case 'ArrowDown':
                this.nextSlide();
                break;
            case 'ArrowUp':
                this.previousSlide();
                break;
            case 'Home':
                this.goToSlide(0);
                break;
            case 'End':
                this.goToSlide(this.slides.length - 1);
                break;
            case 'Escape':
                this.stopPresenting();
                break;
            case 'f':
            case 'F':
                this.toggleFullscreen();
                break;
            default:
                handled = false;
        }

        if (handled) {
            event.preventDefault();
            event.stopPropagation();
            // The report document has its own listeners and the parent has the SPA's;
            // a step must not also scroll the page or trigger a report's own handler.
            this.cdr.detectChanges();
        }
    };

    /** Keep section nav inside the report iframe (never route the parent SPA). */
    private wireIframeNavigation(iframe: HTMLIFrameElement): void {
        const doc = iframe.contentDocument;
        if (!doc || iframe.dataset['reportNavWired'] === 'true') {
            return;
        }
        iframe.dataset['reportNavWired'] = 'true';

        doc.addEventListener(
            'click',
            (event) => {
                const target = event.target as Element | null;
                if (!target) {
                    return;
                }

                const anchor = target.closest('a');
                if (anchor) {
                    const href = anchor.getAttribute('href')?.trim() ?? '';
                    if (/^https?:\/\//i.test(href)) {
                        event.preventDefault();
                        event.stopPropagation();
                        window.open(href, '_blank', 'noopener,noreferrer');
                        return;
                    }

                    event.preventDefault();
                    event.stopPropagation();

                    const sectionKey =
                        anchor.getAttribute('data-report-nav') ??
                        href.replace(/^#/, '').split(/[?#]/)[0] ??
                        href.replace(/^[#/]+/, '').split(/[?#]/)[0];

                    if (!sectionKey || href === '#' || href === '') {
                        doc.defaultView?.scrollTo({ top: 0, behavior: 'smooth' });
                        return;
                    }

                    this.scrollToReportSection(doc, sectionKey);
                    return;
                }

                const button = target.closest('button, [role="button"]');
                if (button) {
                    const label = button.textContent?.trim().toLowerCase() ?? '';
                    const sectionKey = REPORT_NAV_LABELS[label];
                    if (sectionKey) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.scrollToReportSection(doc, sectionKey);
                    }
                }
            },
            true
        );
    }

    private scrollToReportSection(doc: Document, key: string): void {
        const normalized = key.toLowerCase().trim();
        const candidates = [
            normalized,
            normalized.replace(/\s+/g, '-'),
            `section-${normalized}`,
            `${normalized}-section`,
        ];

        for (const id of candidates) {
            const target = this.findScrollTarget(doc, id);
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
        }

        const partial = doc.querySelector(
            `[id*="${normalized}" i], [name*="${normalized}" i]`
        );
        partial?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    private findScrollTarget(doc: Document, id: string): Element | null {
        const exact =
            doc.getElementById(id) ??
            doc.querySelector(`[id="${id}"]`) ??
            doc.querySelector(`[name="${id}"]`);
        if (exact) {
            return exact;
        }

        const lower = id.toLowerCase();
        const all = doc.querySelectorAll('[id], [name]');
        for (const node of Array.from(all)) {
            const nodeId = (node.id || node.getAttribute('name') || '').toLowerCase();
            if (nodeId === lower) {
                return node;
            }
        }
        return null;
    }

    private parseResponseBody(body: string): HtmlLoadResult {
        const trimmed = body.trim();
        if (!trimmed) {
            return { kind: 'empty' };
        }

        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                const json = JSON.parse(trimmed);
                const report = json?.data?.report ?? json?.report ?? json;
                if (typeof report === 'string' && report.includes('<')) {
                    return { kind: 'html', html: report };
                }
                const htmlField =
                    report?.html ??
                    report?.reportHtml ??
                    report?.report_html ??
                    report?.content;
                if (typeof htmlField === 'string' && htmlField.trim()) {
                    return { kind: 'html', html: htmlField };
                }
                return { kind: 'legacy' };
            } catch {
                return { kind: 'empty' };
            }
        }

        if (trimmed.includes('<')) {
            return { kind: 'html', html: body };
        }

        return { kind: 'empty' };
    }
}

